/**
 * Рахунки обліку договорів (0372, Норма #25) — сторінка дефолтів + правил.
 *
 * Дзеркало PayrollAccountRulesPage:
 * 1. «Дефолти за видом договору» — org-wide правила contract_type
 *    (розрахунки + аванси). Порожньо = стандарт (361/631, нерезидент-авто).
 * 2. «Нестандартні рахунки договорів» — договори з персональними правилами
 *    (вкладка «Рахунки обліку» картки пише правило contract_id).
 *    ⚠ якщо рахунок непридатний (група/забаланс) — резолвер його ігнорує.
 */
import { useEffect, useMemo } from 'react';
import { useOrgFilter, pickDefaultOrg } from '@/store/defaults';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { orgs } from '@/api/catalogs';
import { accounts } from '@/api/documents';
import {
  contractAccountRules,
  type ContractAccountRule,
  PURPOSE_LABELS,
} from '@/api/contractAccountRules';
import AccountSelect from '@/components/AccountSelect';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';

const PERMISSION = 'refs.contractors.edit';

type Vid = {
  contract_type: string;
  label: string;
  settlement_col: 'receivable_account_id' | 'payable_account_id';
  advance_col: 'advance_received_account_id' | 'advance_issued_account_id';
  // П3 (0376): розрахунки за зворотною тарою (застава).
  tare_col: 'tare_receivable_account_id' | 'tare_payable_account_id';
  settlement_default: string;
  advance_default: string;
  tare_default: string;
};

const VIDY: Vid[] = [
  {
    contract_type: 'WITH_BUYER', label: 'З покупцем',
    settlement_col: 'receivable_account_id', advance_col: 'advance_received_account_id',
    tare_col: 'tare_receivable_account_id',
    settlement_default: '361 (362 нерезидент)', advance_default: '6811 (6812 валюта)',
    tare_default: '3771',
  },
  {
    contract_type: 'WITH_SUPPLIER', label: 'З постачальником',
    settlement_col: 'payable_account_id', advance_col: 'advance_issued_account_id',
    tare_col: 'tare_payable_account_id',
    settlement_default: '631 (632 нерезидент)', advance_default: '3711',
    tare_default: '6851',
  },
  {
    contract_type: 'OTHER', label: 'Інше',
    settlement_col: 'receivable_account_id', advance_col: 'advance_received_account_id',
    tare_col: 'tare_receivable_account_id',
    settlement_default: '361/631 за документом', advance_default: '6811/3711',
    tare_default: '3771/6851',
  },
];

export default function ContractAccountRulesPage() {
  const qc = useQueryClient();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['contract-account-rules', orgId],
    queryFn: () => contractAccountRules.list({ organization_id: orgId }),
    enabled: !!orgId,
  });
  const nonstd = useQuery({
    queryKey: ['contract-account-rules-nonstd', orgId],
    queryFn: () => contractAccountRules.nonstandard({ organization_id: orgId }),
    enabled: !!orgId,
  });
  const accountsQ = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => accounts.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });

  const accountCode = useMemo(() => {
    const m = new Map<string, string>();
    accountsQ.data?.forEach((a) => m.set(a.id, a.code));
    return m;
  }, [accountsQ.data]);
  const accountUsable = useMemo(() => {
    const m = new Map<string, boolean>();
    accountsQ.data?.forEach((a) => m.set(a.id, !a.is_group_only && !a.is_off_balance));
    return m;
  }, [accountsQ.data]);

  const canManage = !!orgId && allowedOrgIds.has(orgId);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['contract-account-rules', orgId] });
    qc.invalidateQueries({ queryKey: ['contract-account-rules-nonstd', orgId] });
  };

  // org-wide правило виду (без contract/contractor вимірів).
  const findTypeRule = (contract_type: string): ContractAccountRule | null =>
    (list.data ?? []).find(
      (r) => !r.contract_id && !r.contractor_id
        && r.contract_type === contract_type && r.is_active,
    ) ?? null;

  const upsert = useMutation({
    mutationFn: async (p: {
      vid: Vid; col: string; accountId: string | null;
    }) => {
      const rule = findTypeRule(p.vid.contract_type);
      if (rule) {
        return contractAccountRules.patch(rule.id, { [p.col]: p.accountId });
      }
      if (!p.accountId) return null;
      return contractAccountRules.create({
        organization_id: orgId,
        contract_type: p.vid.contract_type,
        [p.col]: p.accountId,
        comment: 'Дефолт виду договору',
      });
    },
    onSuccess: invalidate,
  });

  type DefRow = {
    vid: Vid;
    settlement: string | null;
    advance: string | null;
    tare: string | null;
  };
  const defRows: DefRow[] = VIDY.map((vid) => {
    const rule = findTypeRule(vid.contract_type);
    return {
      vid,
      settlement: rule ? (rule as any)[vid.settlement_col] : null,
      advance: rule ? (rule as any)[vid.advance_col] : null,
      tare: rule ? (rule as any)[vid.tare_col] : null,
    };
  });

  const defCols: ColumnDef<DefRow>[] = [
    { key: 'vid', header: 'Вид договору', cell: (r) => r.vid.label, width: 200 },
    {
      key: 'settlement', header: 'Рахунок розрахунків',
      cell: (r) => canManage ? (
        <AccountSelect
          value={r.settlement ?? ''}
          onChange={(v) => upsert.mutate({ vid: r.vid, col: r.vid.settlement_col, accountId: v || null })}
          placeholder={`стандарт: ${r.vid.settlement_default}`}
        />
      ) : (r.settlement ? accountCode.get(r.settlement) : r.vid.settlement_default),
    },
    {
      key: 'advance', header: 'Рахунок авансів',
      cell: (r) => canManage ? (
        <AccountSelect
          value={r.advance ?? ''}
          onChange={(v) => upsert.mutate({ vid: r.vid, col: r.vid.advance_col, accountId: v || null })}
          placeholder={`стандарт: ${r.vid.advance_default}`}
        />
      ) : (r.advance ? accountCode.get(r.advance) : r.vid.advance_default),
    },
    {
      key: 'tare', header: 'Рахунок тари (застава)',
      cell: (r) => canManage ? (
        <AccountSelect
          value={r.tare ?? ''}
          onChange={(v) => upsert.mutate({ vid: r.vid, col: r.vid.tare_col, accountId: v || null })}
          placeholder={`стандарт: ${r.vid.tare_default}`}
        />
      ) : (r.tare ? accountCode.get(r.tare) : r.vid.tare_default),
    },
  ];

  type NonstdRow = { contractor: string; contract: string; accountsText: string; warn: boolean };
  const nonstdRows: NonstdRow[] = (nonstd.data ?? []).map((n) => ({
    contractor: n.contractor_name,
    contract: n.contract_name,
    accountsText: n.accounts
      .map((a) => `${PURPOSE_LABELS[a.purpose] ?? a.purpose}: ${a.code ?? '?'}`)
      .join(' · '),
    warn: n.accounts.some(
      (a) => a.source === 'rule:unusable'
        || (a.account_id ? accountUsable.get(a.account_id) === false : false),
    ),
  }));

  const nonstdCols: ColumnDef<NonstdRow>[] = [
    { key: 'contractor', header: 'Контрагент', cell: (r) => r.contractor, width: 260 },
    { key: 'contract', header: 'Договір', cell: (r) => r.contract, width: 240 },
    {
      key: 'accountsText', header: 'Персональні рахунки',
      cell: (r) => (
        <span>
          {r.warn && (
            <span title="Рахунок непридатний (група/забаланс) — не застосовується"
              style={{ color: 'var(--badge-amber-fg)', marginRight: 6 }}>⚠</span>
          )}
          {r.accountsText}
        </span>
      ),
    },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <h2 style={{ margin: 0 }}>Рахунки обліку договорів</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        {!canManage && orgId && (
          <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            Немає права редагувати у цій юрособі — перегляд
          </span>
        )}
      </div>

      <p style={{ color: 'var(--muted)', maxWidth: 720 }}>
        Дефолтні рахунки розрахунків за видом договору. Порожнє поле — діє
        стандарт (361/631, для нерезидента 362/632 автоматично). Персональний
        рахунок конкретного договору задається на його картці (вкладка
        «Рахунки обліку») і зʼявляється у списку нижче.
      </p>

      <h3>Дефолти за видом договору</h3>
      <DataTable rows={defRows} columns={defCols} rowKey={(r) => r.vid.contract_type}
        loading={list.isLoading} emptyText="—" />

      <h3 style={{ marginTop: '1.5rem' }}>Нестандартні рахунки договорів</h3>
      <DataTable rows={nonstdRows} columns={nonstdCols}
        rowKey={(r) => r.contractor + r.contract}
        loading={nonstd.isLoading}
        emptyText="Немає договорів з персональними рахунками" />
    </div>
  );
}
